/**
 * SISP Payment Service
 * Service for interacting with SISP payment gateway API
 */

import api from './api';
import type { PaymentServiceType } from '../types';

// Types
export interface SISPCardData {
  pan: string;           // Card number
  dateMonth: string;     // Expiry month (01-12)
  dateYear: string;      // Expiry year (last 2 digits)
  cvv2: string;          // CVV/CVC code
  cardHolderName?: string;
}

export interface SISPPaymentRequest {
  facturaEntidadeId?: string;
  amount: number;           // Amount in ECV
  origem: PaymentServiceType;
  formaPagamento?: 'visa' | 'mastercard' | 'vinti4';
  cardData?: SISPCardData;
  tokenId?: string;         // For tokenized payments
  entityCode?: string;      // SISP entity code
  referenceNumber?: string; // Payment reference
  entidadeId?: string;      // Internal entity ID for POS config
  transactionCode?: string; // 1=compra, 2=serviço, 3=recarga, 5=token, 6=compra token, 7=cancelar token
}

export interface SISPPaymentResponse {
  success: boolean;
  data?: {
    pedidoId: string;
    merchantRef: string;
    status?: string;
    requires3DS?: boolean;
    html?: string;
    redirectUrl?: string;
    transactionId?: string;
    errorCode?: string;
    errorMessage?: string;
  };
  message?: string;
  error?: string;
}

export interface SavedCard {
  id: string;
  token: string;
  lastFourDigits: string;
  cardType: string;
  cardHolder?: string;
  expireMonth?: string;
  expireYear?: string;
  createdAt: string;
  isActive: boolean;
}

export interface TokenizationResponse {
  success: boolean;
  data?: {
    tokenizationUrl: string;
    merchantRef: string;
    sessionId: string;
  };
  error?: string;
}

export type PaymentStatus = 'idle' | 'loading' | 'processing' | 'redirecting' | 'success' | 'failed' | '3ds_required';

/**
 * National BIN database (Bank Identification Numbers)
 * Cards issued by Cape Verdean banks — charged national tariff, not international 2.5%.
 * Ref: BT202308A - BINs Suportados nos Pagamentos de Serviços e Recargas Online
 */
const NATIONAL_BINS: Record<string, { type: string; bank: string }> = {
  // BAI Cabo Verde
  '50284205': { type: 'Vinti4', bank: 'BANCO BAI CABO VERDE' },
  '50284204': { type: 'Vinti4', bank: 'BANCO BAI CABO VERDE' },
  '50284207': { type: 'Vinti4', bank: 'BANCO BAI CABO VERDE' },
  '50284206': { type: 'Vinti4', bank: 'BANCO BAI CABO VERDE' },
  // IIB
  '50418901': { type: 'Vinti4', bank: 'INTERNATIONAL INVESTMENT BANK' },
  '50418903': { type: 'Vinti4', bank: 'INTERNATIONAL INVESTMENT BANK' },
  '50418902': { type: 'Vinti4', bank: 'INTERNATIONAL INVESTMENT BANK' },
  '50418900': { type: 'Vinti4', bank: 'INTERNATIONAL INVESTMENT BANK' },
  '45329900': { type: 'Visa', bank: 'INTERNATIONAL INVESTMENT BANK' },
  '45328900': { type: 'Visa', bank: 'INTERNATIONAL INVESTMENT BANK' },
  // BCN
  '51583905': { type: 'MasterCard', bank: 'BANCO CABOVERDIANO DE NEGOCIOS' },
  // CECV
  '42244250': { type: 'Visa', bank: 'CAIXA ECONOMICA DE CABO VERDE' },
  '47063703': { type: 'Visa', bank: 'CAIXA ECONOMICA DE CABO VERDE' },
  '47063702': { type: 'Visa', bank: 'CAIXA ECONOMICA DE CABO VERDE' },
  '47063701': { type: 'Visa', bank: 'CAIXA ECONOMICA DE CABO VERDE' },
  '47063700': { type: 'Visa', bank: 'CAIXA ECONOMICA DE CABO VERDE' },
  '42244200': { type: 'Visa', bank: 'CAIXA ECONOMICA DE CABO VERDE' },
  '60344516': { type: 'Vinti4', bank: 'CAIXA ECONOMICA DE CABO VERDE' },
  '60344514': { type: 'Vinti4', bank: 'CAIXA ECONOMICA DE CABO VERDE' },
  '60344511': { type: 'Vinti4', bank: 'CAIXA ECONOMICA DE CABO VERDE' },
  '60344517': { type: 'Vinti4', bank: 'CAIXA ECONOMICA DE CABO VERDE' },
  '60344599': { type: 'Vinti4', bank: 'CAIXA ECONOMICA DE CABO VERDE' },
  '60344515': { type: 'Vinti4', bank: 'CAIXA ECONOMICA DE CABO VERDE' },
  '60344512': { type: 'Vinti4', bank: 'CAIXA ECONOMICA DE CABO VERDE' },
  // Ecobank
  '60572440': { type: 'Vinti4', bank: 'ECOBANK CABO VERDE' },
  '60574515': { type: 'Vinti4', bank: 'ECOBANK CABO VERDE' },
  '60574513': { type: 'Vinti4', bank: 'ECOBANK CABO VERDE' },
  '60574512': { type: 'Vinti4', bank: 'ECOBANK CABO VERDE' },
  '60574511': { type: 'Vinti4', bank: 'ECOBANK CABO VERDE' },
};

// SISP Error Code Messages (spec v4.6 + ISO 8583)
export const SISP_ERROR_MESSAGES: Record<string, string> = {
  // Spec v4.6 merchantRespErrorCode
  '0': 'Erro indefinido. Tente novamente.',
  '1': 'Erro no processamento. Tente novamente mais tarde.',
  '2': 'Autenticação 3D Secure falhou. Tente com outro cartão.',
  '3': 'Erro no processamento da transação.',
  '4': 'Dados inválidos. Verifique os dados e tente novamente.',
  // ISO 8583 response codes
  '000': 'Transação aprovada',
  '001': 'Contactar o emissor do cartão',
  '003': 'Comerciante inválido',
  '004': 'Retenha o cartão',
  '005': 'Transação não autorizada',
  '012': 'Transação inválida',
  '013': 'Montante inválido',
  '014': 'Cartão inválido',
  '030': 'Erro de formato',
  '041': 'Cartão perdido - reter',
  '043': 'Cartão roubado - reter',
  '051': 'Saldo insuficiente',
  '054': 'Cartão expirado',
  '055': 'PIN incorreto',
  '057': 'Transação não permitida',
  '058': 'Transação não permitida ao terminal',
  '061': 'Limite de levantamento excedido',
  '062': 'Cartão restrito',
  '065': 'Limite de transações excedido',
  '075': 'Número de tentativas de PIN excedido',
  '091': 'Emissor indisponível',
  '096': 'Sistema indisponível',
  '999': 'Erro desconhecido',
};

// SISP messageType labels (spec v4.6 + tokenização v1.1)
export const SISP_MESSAGE_TYPES: Record<string, string> = {
  '8': 'Compra aprovada',
  '10': 'Estorno aprovado',
  '6': 'Erro',
  'P': 'Pagamento de serviço aprovado',
  'M': 'Recarga aprovada',
  'A': 'Token criado',
  'B': 'Pagamento com token aprovado',
  'C': 'Token cancelado',
};

class SISPService {
  /**
   * Initiate a card payment
   */
  async initiatePayment(request: SISPPaymentRequest): Promise<SISPPaymentResponse> {
    try {
      const response = await api.post<SISPPaymentResponse>('/sisp/payments', {
        facturaEntidadeId: request.facturaEntidadeId,
        amount: request.amount,
        origem: request.origem,
        formaPagamento: request.formaPagamento || 'visa',
        cardData: request.cardData,
        entityCode: request.entityCode,
        referenceNumber: request.referenceNumber,
        entidadeId: request.entidadeId,
        transactionCode: request.transactionCode,
      });

      return response.data;
    } catch (error: any) {
      console.error('SISP payment initiation error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao iniciar pagamento',
      };
    }
  }

  /**
   * Pay using a saved card token
   */
  async payWithToken(request: {
    tokenId: string;
    amount: number;
    origem?: PaymentServiceType;
    facturaEntidadeId?: string;
    entityCode?: string;
    referenceNumber?: string;
    entidadeId: string;
  }): Promise<SISPPaymentResponse> {
    try {
      const response = await api.post<SISPPaymentResponse>('/sisp/payments/token', {
        tokenId: request.tokenId,
        amount: request.amount,
        origem: request.origem || 'WEBS',
        facturaEntidadeId: request.facturaEntidadeId,
        entityCode: request.entityCode,
        referenceNumber: request.referenceNumber,
        entidadeId: request.entidadeId,
      });

      return response.data;
    } catch (error: any) {
      console.error('SISP token payment error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao processar pagamento',
      };
    }
  }

  /**
   * Get payment status
   */
  async getPaymentStatus(pedidoId: string): Promise<{
    success: boolean;
    status?: string;
    data?: any;
    error?: string;
  }> {
    try {
      const response = await api.get(`/sisp/payments/${pedidoId}`);
      return {
        success: response.data.success,
        status: response.data.data?.ESTADO,
        data: response.data.data,
      };
    } catch (error: any) {
      console.error('Error getting payment status:', error);
      return {
        success: false,
        error: error.message || 'Erro ao verificar estado do pagamento',
      };
    }
  }

  /**
   * Get saved cards (tokens) for the authenticated user
   */
  async getSavedCards(): Promise<{
    success: boolean;
    data?: SavedCard[];
    error?: string;
  }> {
    try {
      const response = await api.get('/sisp/tokens');

      if (response.data.success && response.data.data) {
        const cards: SavedCard[] = response.data.data.map((token: any) => ({
          id: token.ID || token.id,
          token: token.TOKEN || token.token,
          lastFourDigits: token.PAN_LAST_FOUR || token.panLastFour || '****',
          cardType: token.CARD_TYPE || token.cardType || 'VISA',
          cardHolder: token.CARD_HOLDER || token.cardHolder,
          expireMonth: token.EXPIRE_MONTH || token.expireMonth,
          expireYear: token.EXPIRE_YEAR || token.expireYear,
          createdAt: token.DT_REGISTO || token.createdAt,
          isActive: token.ACTIVO === 'S' || token.activo === 'S' || token.isActive,
        }));
        return { success: true, data: cards };
      }

      return { success: true, data: [] };
    } catch (error: any) {
      console.error('Error loading saved cards:', error);
      return {
        success: false,
        data: [],
        error: error.message || 'Erro ao carregar cartões guardados',
      };
    }
  }

  /**
   * Delete a saved card
   */
  async deleteSavedCard(cardId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await api.delete(`/sisp/tokens/${cardId}`);
      return { success: response.data.success };
    } catch (error: any) {
      console.error('Error deleting saved card:', error);
      return {
        success: false,
        error: error.message || 'Erro ao remover cartão',
      };
    }
  }

  /**
   * Initiate card tokenization (add new card to wallet)
   */
  async initiateTokenization(params?: {
    maxTransactions?: number;
    maxAmount?: number;
    entidadeId?: string;
  }): Promise<TokenizationResponse> {
    try {
      const response = await api.post('/sisp/tokens', {
        maxTransactions: params?.maxTransactions || 100,
        maxAmount: params?.maxAmount || 1000000,
        entidadeId: params?.entidadeId,
      });

      return {
        success: response.data.success,
        data: response.data.data,
        error: response.data.error,
      };
    } catch (error: any) {
      console.error('Error initiating tokenization:', error);
      return {
        success: false,
        error: error.message || 'Erro ao iniciar tokenização',
      };
    }
  }

  /**
   * Validate card number using Luhn algorithm
   */
  validateCardNumber(cardNumber: string): boolean {
    const cleanNumber = cardNumber.replace(/\s|-/g, '');
    if (!/^\d{13,19}$/.test(cleanNumber)) return false;

    let sum = 0;
    let isEven = false;

    for (let i = cleanNumber.length - 1; i >= 0; i--) {
      let digit = parseInt(cleanNumber[i], 10);

      if (isEven) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }

      sum += digit;
      isEven = !isEven;
    }

    return sum % 10 === 0;
  }

  /**
   * Detect card type from number
   */
  detectCardType(cardNumber: string): 'visa' | 'mastercard' | 'vinti4' | 'unknown' {
    const cleanNumber = cardNumber.replace(/\s|-/g, '');

    // Check national BINs first (Vinti4 cards from Cape Verdean banks)
    const binInfo = this.getNationalBinInfo(cleanNumber);
    if (binInfo && binInfo.type === 'Vinti4') return 'vinti4';

    if (/^4/.test(cleanNumber)) return 'visa';
    if (/^5[1-5]/.test(cleanNumber) || /^2[2-7]/.test(cleanNumber)) return 'mastercard';

    return 'unknown';
  }

  /**
   * Check if a card is national (Cape Verdean bank issued).
   * National cards use local tariff instead of international 2.5%.
   * Ref: BT202308A - BINs Suportados nos Pagamentos de Serviços e Recargas Online
   */
  isNationalCard(cardNumber: string): boolean {
    return this.getNationalBinInfo(cardNumber) !== null;
  }

  /**
   * Get national BIN info for a card number
   */
  getNationalBinInfo(cardNumber: string): { type: string; bank: string } | null {
    const cleanNumber = cardNumber.replace(/[\s-]/g, '');
    if (cleanNumber.length < 8) return null;

    const bin = cleanNumber.substring(0, 8);
    return NATIONAL_BINS[bin] || null;
  }

  /**
   * Format card number for display (groups of 4)
   */
  formatCardNumber(cardNumber: string): string {
    const cleanNumber = cardNumber.replace(/\s|-/g, '');
    return cleanNumber.replace(/(.{4})/g, '$1 ').trim();
  }

  /**
   * Mask card number for display
   */
  maskCardNumber(cardNumber: string): string {
    const cleanNumber = cardNumber.replace(/\s|-/g, '');
    if (cleanNumber.length < 4) return cardNumber;
    const lastFour = cleanNumber.slice(-4);
    return `•••• •••• •••• ${lastFour}`;
  }

  /**
   * Get error message for SISP error code
   */
  getErrorMessage(errorCode: string): string {
    return SISP_ERROR_MESSAGES[errorCode] || 'Erro desconhecido. Por favor tente novamente.';
  }

  /**
   * Store pending payment info in session
   */
  storePendingPayment(paymentData: {
    pedidoId: string;
    merchantRef: string;
    amount: number;
  }): void {
    sessionStorage.setItem('pendingSispPayment', JSON.stringify(paymentData));
  }

  /**
   * Get pending payment info from session
   */
  getPendingPayment(): {
    pedidoId: string;
    merchantRef: string;
    amount: number;
  } | null {
    const data = sessionStorage.getItem('pendingSispPayment');
    if (!data) return null;
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }

  /**
   * Clear pending payment from session
   */
  clearPendingPayment(): void {
    sessionStorage.removeItem('pendingSispPayment');
  }
}

const sispService = new SISPService();
export default sispService;
